// ═══════════════════════════════════════════════════════════════
// HotWash — Analytics
// AI summary, sentiment analysis, charts, and breakdowns
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Select, Group, Stack, Progress, Badge,
  RingProgress, Center, ThemeIcon, Button, Divider,
} from '../../components/gab/GabComponents';
import { IconSparkles, IconTrendingUp, IconTrendingDown, IconMinus } from '@tabler/icons-react';
import { useHotWashStore } from '../../store/hotwashStore';
import { useAuthStore } from '../../store/authStore';
import { TIER_META, TIER_KEYS, CATEGORIES, PHASES } from '../../types';
import type { Observation } from '../../types';
import AISummaryPanel from '../../components/hotwash/AISummaryPanel';
import AIAnalysis from '../../components/hotwash/AIAnalysis';
import { primary, secondary, semantic } from '../../lib/colors';

function getSentiment(obs: Observation[]): { score: number; label: string; color: string; trend: string } {
  if (obs.length === 0) return { score: 0, label: 'No Data', color: secondary[700], trend: 'neutral' };
  const total = obs.length;
  const positive = obs.filter(o => o.tier === 'observation').length;
  const negative = obs.filter(o => o.tier === 'critical_error' || o.tier === 'problem_identified').length;

  const positiveRatio = positive / total;
  const negativeRatio = negative / total;

  let score = Math.round((positiveRatio * 100) - (negativeRatio * 100) + 50);
  score = Math.max(0, Math.min(100, score));

  const label = score >= 70 ? 'Positive' : score >= 40 ? 'Mixed' : 'Concerning';
  const color = score >= 70 ? 'var(--color-success)' : score >= 40 ? 'var(--color-warning)' : semantic.error;
  const trend = positiveRatio > negativeRatio ? 'up' : positiveRatio < negativeRatio ? 'down' : 'neutral';

  return { score, label, color, trend };
}

export default function Analytics() {
  const { observations, loading: isLoading, loadObservations } = useHotWashStore();
  const { incidents, activeIncident } = useAuthStore();
  const [incidentFilter, setIncidentFilter] = useState<string>(
    activeIncident ? String(activeIncident.id) : 'all'
  );
  const [showAIReport, setShowAIReport] = useState(false);

  useEffect(() => { loadObservations(); }, [loadObservations]);

  const incidentOptions = [
    { value: 'all', label: 'All Incidents' },
    ...incidents.filter(i => i.id != null).map(i => ({ value: String(i.id), label: i.name })),
  ];

  const filtered = incidentFilter === 'all'
    ? observations
    : observations.filter(o => String(o.incidentId) === incidentFilter);

  const selectedIncident = incidentFilter !== 'all'
    ? incidents.find(i => String(i.id) === incidentFilter) ?? null
    : activeIncident ?? null;

  const total = filtered.length;

  if (total === 0 && !isLoading) {
    return (
      <div>
        <Group justify="space-between" mb="md">
          <Title order={3} c="black">Analytics</Title>
          <Select data={incidentOptions} value={incidentFilter} onChange={v => setIncidentFilter(v ?? 'all')} size="xs" w={220} />
        </Group>
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No observations to analyze. Submit some observations first.</Text>
        </Paper>
      </div>
    );
  }

  // Tier distribution
  const tierData = TIER_KEYS.map(t => ({
    tier: t,
    count: filtered.filter(o => o.tier === t).length,
    pct: total > 0 ? Math.round((filtered.filter(o => o.tier === t).length / total) * 100) : 0,
    ...(TIER_META[t] ?? TIER_META['observation']),
  }));

  // Category breakdown
  const catData = CATEGORIES.map(c => ({
    category: c,
    count: filtered.filter(o => o.category === c).length,
    pct: total > 0 ? Math.round((filtered.filter(o => o.category === c).length / total) * 100) : 0,
  })).filter(c => c.count > 0).sort((a, b) => b.count - a.count);

  // Phase breakdown
  const phaseData = PHASES.map(p => ({
    phase: p,
    count: filtered.filter(o => o.phase === p).length,
    pct: total > 0 ? Math.round((filtered.filter(o => o.phase === p).length / total) * 100) : 0,
  })).filter(p => p.count > 0).sort((a, b) => b.count - a.count);

  // Remedy coverage
  const withRemedy = filtered.filter(o => o.suggestedRemedy).length;
  const remedyPct = total > 0 ? Math.round((withRemedy / total) * 100) : 0;

  // Sentiment
  const sentiment = getSentiment(filtered as any);

  // Contributors
  const contributors = [...new Set(filtered.map(o => o.submittedBy))];

  // Per-incident breakdown
  const incidentBreakdown = incidents
    .filter(i => i.id != null)
    .map(i => ({
      incident: i,
      count: observations.filter(o => String(o.incidentId) === String(i.id)).length,
      critical: observations.filter(o => String(o.incidentId) === String(i.id) && o.tier === 'critical_error').length,
      remedied: observations.filter(o => String(o.incidentId) === String(i.id) && o.suggestedRemedy).length,
    }))
    .filter(i => i.count > 0)
    .sort((a, b) => b.count - a.count);

  // Missing remedies on critical/problem items
  const unremediedCritical = filtered.filter(o => !o.suggestedRemedy && (o.tier === 'critical_error' || o.tier === 'problem_identified'));

  const TrendIcon = sentiment.trend === 'up' ? IconTrendingUp : sentiment.trend === 'down' ? IconTrendingDown : IconMinus;

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Analytics</Title>
        <Select data={incidentOptions} value={incidentFilter} onChange={v => setIncidentFilter(v ?? 'all')} size="xs" w={220} />
      </Group>

      {/* AI Summary */}
      <div style={{ marginBottom: 16 }}>
        <AISummaryPanel observations={filtered as any} />
      </div>

      {/* Key Metrics Row */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder ta="center">
          <Text fz={28} fw={700}>{total}</Text>
          <Text size="xs" c="dimmed">Total Observations</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <Text fz={28} fw={700} c={semantic.error}>
            {filtered.filter(o => o.tier === 'critical_error').length}
          </Text>
          <Text size="xs" c="dimmed">Critical Errors</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <Text fz={28} fw={700} c={semantic.success}>{remedyPct}%</Text>
          <Text size="xs" c="dimmed">Remedy Coverage</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center">
          <Text fz={28} fw={700} c={semantic.info}>{contributors.length}</Text>
          <Text size="xs" c="dimmed">Contributors</Text>
        </Paper>
      </SimpleGrid>

      {/* Sentiment + Remedy Row */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {/* Sentiment Analysis */}
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb="sm">
            <IconSparkles size={16} color={primary[700]} />
            <Text fw={600} size="sm">Sentiment Analysis</Text>
          </Group>
          <Group gap="lg">
            <RingProgress
              size={90}
              thickness={8}
              roundCaps
              sections={[{ value: sentiment.score, color: sentiment.color }]}
              label={
                <Center>
                  <div style={{ textAlign: 'center' }}>
                    <Text fw={700} fz={20} style={{ color: sentiment.color, lineHeight: 1 }}>{sentiment.score}</Text>
                    <Text fz={9} c="dimmed">/100</Text>
                  </div>
                </Center>
              }
            />
            <div style={{ flex: 1 }}>
              <Group gap="xs" mb="xs">
                <Badge size="sm" style={{ background: sentiment.color + '15', color: sentiment.color }}>
                  {sentiment.label}
                </Badge>
                <ThemeIcon size="xs" variant="light" color={sentiment.trend === 'up' ? 'green' : sentiment.trend === 'down' ? 'red' : 'gray'} radius="xl">
                  <TrendIcon size={10} />
                </ThemeIcon>
              </Group>
              <SimpleGrid cols={3}>
                <div>
                  <Text fz={10} c="dimmed">Positive</Text>
                  <Text fw={600} size="sm" c={semantic.success}>
                    {filtered.filter(o => o.tier === 'observation').length} ({total > 0 ? Math.round((filtered.filter(o => o.tier === 'observation').length / total) * 100) : 0}%)
                  </Text>
                </div>
                <div>
                  <Text fz={10} c="dimmed">Neutral</Text>
                  <Text fw={600} size="sm" c={semantic.warning}>
                    {filtered.filter(o => o.tier === 'improvement_needed').length}
                  </Text>
                </div>
                <div>
                  <Text fz={10} c="dimmed">Negative</Text>
                  <Text fw={600} size="sm" c={semantic.error}>
                    {filtered.filter(o => o.tier === 'critical_error' || o.tier === 'problem_identified').length}
                  </Text>
                </div>
              </SimpleGrid>
            </div>
          </Group>
        </Paper>

        {/* Remedy Coverage */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Remedy Coverage</Text>
          <Group justify="space-between" mb={4}>
            <Text size="sm">{withRemedy} of {total} have remedies</Text>
            <Text size="sm" fw={600} style={{ color: remedyPct > 70 ? 'var(--color-success)' : remedyPct > 40 ? 'var(--color-warning)' : semantic.error }}>
              {remedyPct}%
            </Text>
          </Group>
          <Progress
            value={remedyPct}
            color={remedyPct > 70 ? 'var(--color-success)' : remedyPct > 40 ? 'var(--color-warning)' : semantic.error}
            size="lg"
            radius="xl"
            mb="sm"
          />
          {unremediedCritical.length > 0 && (
            <Paper p="xs" radius="sm" bg="var(--error-light)" style={{ border: '1px solid var(--error)' }}>
              <Text size="xs" c="var(--error)" fw={500}>
                {unremediedCritical.length} critical/problem observation{unremediedCritical.length !== 1 ? 's' : ''} missing remedies
              </Text>
            </Paper>
          )}
        </Paper>
      </SimpleGrid>

      {/* Distribution Charts */}
      <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
        {/* Tier Distribution */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Tier Distribution</Text>
          <Stack gap="xs">
            {tierData.map(t => (
              <div key={t.tier}>
                <Group justify="space-between" mb={2}>
                  <Badge size="xs" style={{ background: t.bg, color: t.color }}>{t.label}</Badge>
                  <Text size="xs" fw={500}>{t.count} ({t.pct}%)</Text>
                </Group>
                <Progress value={t.pct} color={t.color} size="sm" radius="xl" />
              </div>
            ))}
          </Stack>
        </Paper>

        {/* Category Breakdown */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">By Category</Text>
          <Stack gap="xs">
            {catData.map(c => (
              <div key={c.category}>
                <Group justify="space-between" mb={2}>
                  <Text size="xs" tt="capitalize">{c.category}</Text>
                  <Text size="xs" fw={500}>{c.count} ({c.pct}%)</Text>
                </Group>
                <Progress value={c.pct} color="blurple" size="sm" radius="xl" />
              </div>
            ))}
          </Stack>
        </Paper>

        {/* Phase Breakdown */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">By Phase</Text>
          <Stack gap="xs">
            {phaseData.map(p => (
              <div key={p.phase}>
                <Group justify="space-between" mb={2}>
                  <Text size="xs" tt="capitalize">{p.phase}</Text>
                  <Text size="xs" fw={500}>{p.count} ({p.pct}%)</Text>
                </Group>
                <Progress value={p.pct} color="cyan" size="sm" radius="xl" />
              </div>
            ))}
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* AI Narrative Report */}
      {selectedIncident && filtered.length > 0 && (
        <Paper p="md" radius="sm" withBorder mb="md">
          <Group justify="space-between" mb={showAIReport ? 'md' : 0}>
            <Group gap="xs">
              <IconSparkles size={18} color={primary[700]} />
              <Text fw={600} size="sm">AI After-Action Report</Text>
            </Group>
            <Button
              size="xs"
              variant={showAIReport ? 'light' : 'filled'}
              leftSection={<IconSparkles size={14} />}
              onClick={() => setShowAIReport(!showAIReport)}
            >
              {showAIReport ? 'Hide Report' : 'Generate AI Report'}
            </Button>
          </Group>
          {!showAIReport && (
            <Text size="sm" c="dimmed" mt="xs">
              Generate a comprehensive AI-powered narrative report with executive summary, top strengths & weaknesses, priority action items, and recommendations.
            </Text>
          )}
          {showAIReport && (
            <>
              <Divider mb="md" />
              <AIAnalysis incident={selectedIncident as any} observations={filtered as any} />
            </>
          )}
        </Paper>
      )}

      {/* Per-Incident Table */}
      {incidentBreakdown.length > 0 && (
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Observations by Incident</Text>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--frost)' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Incident</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Total</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Critical</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Remedied</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {incidentBreakdown.map(row => {
                  const coverage = row.count > 0 ? Math.round((row.remedied / row.count) * 100) : 0;
                  return (
                    <tr key={row.incident.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '8px 12px' }}>{row.incident.name}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <Badge size="xs" color={row.incident.status === 'active' ? 'green' : row.incident.status === 'closed' ? 'gray' : 'yellow'} tt="capitalize">
                          {row.incident.status}
                        </Badge>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{row.count}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: row.critical > 0 ? 'var(--color-error)' : undefined }}>
                        {row.critical}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{row.remedied}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <Text size="xs" fw={500} style={{ color: coverage > 70 ? 'var(--color-success)' : coverage > 40 ? 'var(--color-warning)' : semantic.error }}>
                          {coverage}%
                        </Text>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Paper>
      )}
    </div>
  );
}
